// Must be the same Vue build as Aeppic (externals/vue.js). Importing 'vue'
// resolves to a different file in some bundles, and watchers created via
// Aeppic.Vue.$watch then never see changes to these observables.
import Vue from '../externals/vue.js'
import type Aeppic from './aeppic.js'
import { findClosestElement } from './utils/dom-traversal.js'

type Key = string // '<kind>:<name>'

const UNMOUNTED_CONTEXT_OBJECT_METHODS = new Set(['get', 'find', 'getInScope', 'getInNamedScope'])
const UNMOUNTED_CONTEXT_ARRAY_METHODS = new Set(['findAllOfKind', 'findAllOfKindInScope', 'findAllOfKindInNamedScope'])
const UNMOUNTED_CONTEXT_UNWATCH_METHODS = new Set(['watch', 'watchInScope', 'watchInNamedScope'])

/**
 * Public API bound to a component/element.
 * All operations are element-relative → deterministic scope resolution.
 */
export class SharedContextApiForComponents {
  private _unwatchers: Set<() => void> = new Set()
  private _released = false

  constructor(
    private _el: HTMLElement,
    private _ctx: SharedContext
  ) {}

  private assertAlive() {
    if (this._released) {
      throw new Error('SharedContextApiForComponents has been released')
    }
  }

  release() {
    if (this._released) return
    this._released = true

    this._unwatchers.forEach(unwatch => unwatch())
    this._unwatchers.clear()

    // break references for GC
    ;(this as any)._ctx = null
    ;(this as any)._el = null
  }

  // -------- set --------

  /**
   * Replace entire value (clears previous keys).
   * set('kind:name', value)
   * set('scopeName', 'kind:name', value)
   */
  set(kindColonName: string, value: any): any
  set(scope: string, kindColonName: string, value: any): any
  set(a: string, b: any, c?: any) {
    this.assertAlive()

    if (c === undefined) {
      return this._ctx.replace(this._el, a, b)
    } else {
      return this._ctx.replaceInNamedScope(this._el, a, b, c)
    }
  }

  setInScope(scope: string, kindColonName: string, value: any) {
    return this.set(scope, kindColonName, value)
  }

  replace(kindColonName: string, value: any): any
  replace(scope: string, kindColonName: string, value: any): any
  replace(a: string, b: any, c?: any) {
    return this.set(a, b, c)
  }

  replaceInScope(scope: string, kindColonName: string, value: any) {
    return this.setInScope(scope, kindColonName, value)
  }

  /**
   * Patch (merge shallowly) into existing object.
   * patch('kind:name', partial)
   * patch('scopeName', 'kind:name', partial)
   */
  patch(kindColonName: string, partial: any): any
  patch(scope: string, kindColonName: string, partial: any): any
  patch(a: string, b: any, c?: any) {
    this.assertAlive()

    if (c === undefined) {
      return this._ctx.patch(this._el, a, b)
    } else {
      return this._ctx.patchInNamedScope(this._el, a, b, c)
    }
  }

  patchInScope(scope: string, kindColonName: string, partial: any) {
    return this.patch(scope, kindColonName, partial)
  }

  // -------- get / find --------

  get(kindColonName: string): any
  get(scope: string, kindColonName: string): any
  get(a: string, b?: string) {
    this.assertAlive()

    return b === undefined
      ? this._ctx.get(this._el, a)
      : this._ctx.getInNamedScope(this._el, a, b)
  }

  find = this.get.bind(this)

  getInScope(scope: string, kindColonName: string) {
    return this.get(scope, kindColonName)
  }

  getInNamedScope(scope: string, kindColonName: string) {
    return this.getInScope(scope, kindColonName)
  }

  /**
   * Snapshot (non-reactive list) of all values for a kind in the resolved scope.
   * (If you need reactivity, build a higher-level derived watcher.)
   */
  findAllOfKind(kind: string): any[]
  findAllOfKind(scope: string, kind: string): any[]
  findAllOfKind(a: string, b?: string) {
    this.assertAlive()

    return b === undefined
      ? this._ctx.findAllOfKind(this._el, a)
      : this._ctx.findAllOfKindInNamedScope(this._el, a, b)
  }

  findAllOfKindInScope(scope: string, kind: string) {
    return this.findAllOfKind(scope, kind)
  }

  findAllOfKindInNamedScope(scope: string, kind: string) {
    return this.findAllOfKindInScope(scope, kind)
  }

  // -------- watch --------

  /**
   * Deep watch of the shared object.
   * NOTE (Vue 2): for deep watches on mutable objects, oldVal/newVal are often the same reference.
   * If you need diffs, compute them yourself or emit patches.
   */
  watch(
    kindColonName: string,
    callback: (newVal: any, oldVal: any) => void
  ): () => void
  watch(
    scope: string,
    kindColonName: string,
    callback: (newVal: any, oldVal: any) => void
  ): () => void
  watch(a: string, b: any, c?: any) {
    this.assertAlive()

    const kindColonName = c === undefined ? a : b
    const callback = c === undefined ? b : c
    const scope = c === undefined ? undefined : a

    const unwatch =
      scope === undefined
        ? this._ctx.watch(this._el, kindColonName, callback)
        : this._ctx.watchInNamedScope(this._el, scope, kindColonName, callback)

    let called = false
    const safeUnwatch = () => {
      if (!called) {
        called = true
        unwatch()
        this._unwatchers.delete(safeUnwatch)
      }
    }

    this._unwatchers.add(safeUnwatch)
    return safeUnwatch
  }

  watchInScope(
    scope: string,
    kindColonName: string,
    callback: (newVal: any, oldVal: any) => void
  ) {
    return this.watch(scope, kindColonName, callback)
  }

  watchInNamedScope(
    scope: string,
    kindColonName: string,
    callback: (newVal: any, oldVal: any) => void
  ) {
    return this.watchInScope(scope, kindColonName, callback)
  }
}

/**
 * Stable API handed to dynamic controller code before the Vue instance is mounted.
 * Calls before mount are lifecycle errors; mounted() binds this wrapper to the
 * component's concrete DOM element.
 */
export class SharedContextApiForDynamicComponent {
  private _api: SharedContextApiForComponents = null
  private _released = false

  constructor(private _ctx: SharedContext) {}

  get isMounted() {
    return Boolean(this._api) && !this._released
  }

  mount(el: HTMLElement) {
    if (this._released) {
      console.error('SharedContext.mount() cannot be called after release().')
      return
    }

    if (!isElement(el) || !el.parentNode) {
      console.error('SharedContext.mount() requires the mounted component DOM element.')
      return
    }

    if (this._api) {
      return
    }

    const api = new SharedContextApiForComponents(el, this._ctx)
    this._api = api

    ;(this as any).set = api.set.bind(api)
    ;(this as any).setInScope = api.setInScope.bind(api)
    ;(this as any).replace = api.replace.bind(api)
    ;(this as any).replaceInScope = api.replaceInScope.bind(api)
    ;(this as any).patch = api.patch.bind(api)
    ;(this as any).patchInScope = api.patchInScope.bind(api)
    ;(this as any).get = api.get.bind(api)
    ;(this as any).find = api.find.bind(api)
    ;(this as any).getInScope = api.getInScope.bind(api)
    ;(this as any).getInNamedScope = api.getInNamedScope.bind(api)
    ;(this as any).findAllOfKind = api.findAllOfKind.bind(api)
    ;(this as any).findAllOfKindInScope = api.findAllOfKindInScope.bind(api)
    ;(this as any).findAllOfKindInNamedScope = api.findAllOfKindInNamedScope.bind(api)
    ;(this as any).watch = api.watch.bind(api)
    ;(this as any).watchInScope = api.watchInScope.bind(api)
    ;(this as any).watchInNamedScope = api.watchInNamedScope.bind(api)
  }

  release() {
    if (this._released) return
    this._released = true
    this._api?.release()
    this._api = null
    ;(this as any)._ctx = null
  }

  set(kindColonName: string, value: any): any
  set(scope: string, kindColonName: string, value: any): any
  set() {
    return this.reportUnmountedCall('set')
  }

  setInScope() {
    return this.reportUnmountedCall('setInScope')
  }

  replace(kindColonName: string, value: any): any
  replace(scope: string, kindColonName: string, value: any): any
  replace() {
    return this.reportUnmountedCall('replace')
  }

  replaceInScope() {
    return this.reportUnmountedCall('replaceInScope')
  }

  patch(kindColonName: string, partial: any): any
  patch(scope: string, kindColonName: string, partial: any): any
  patch() {
    return this.reportUnmountedCall('patch')
  }

  patchInScope() {
    return this.reportUnmountedCall('patchInScope')
  }

  get(kindColonName: string): any
  get(scope: string, kindColonName: string): any
  get() {
    return this.reportUnmountedCall('get')
  }

  find = this.get.bind(this)

  getInScope() {
    return this.reportUnmountedCall('getInScope')
  }

  getInNamedScope() {
    return this.reportUnmountedCall('getInNamedScope')
  }

  findAllOfKind(kind: string): any[]
  findAllOfKind(scope: string, kind: string): any[]
  findAllOfKind() {
    return this.reportUnmountedCall('findAllOfKind')
  }

  findAllOfKindInScope() {
    return this.reportUnmountedCall('findAllOfKindInScope')
  }

  findAllOfKindInNamedScope() {
    return this.reportUnmountedCall('findAllOfKindInNamedScope')
  }

  watch(
    kindColonName: string,
    callback: (newVal: any, oldVal: any) => void
  ): () => void
  watch(
    scope: string,
    kindColonName: string,
    callback: (newVal: any, oldVal: any) => void
  ): () => void
  watch() {
    return this.reportUnmountedCall('watch')
  }

  watchInScope() {
    return this.reportUnmountedCall('watchInScope')
  }

  watchInNamedScope() {
    return this.reportUnmountedCall('watchInNamedScope')
  }

  private reportUnmountedCall(methodName: string) {
    const reason = this._released
      ? 'has already been released'
      : 'is only available after the component is mounted'

    console.error(`SharedContext.${methodName}() ${reason}. Calls before mounted() are ignored.`)

    if (UNMOUNTED_CONTEXT_OBJECT_METHODS.has(methodName)) {
      return Vue.observable({})
    }

    if (UNMOUNTED_CONTEXT_ARRAY_METHODS.has(methodName)) {
      return []
    }

    if (UNMOUNTED_CONTEXT_UNWATCH_METHODS.has(methodName)) {
      return () => {}
    }

    return undefined
  }
}

/**
 * Core store.
 * - Scope identity = DOM Element
 * - Per-scope map of stable observables (never replaced)
 */
export class SharedContext {
  private scopeData: WeakMap<Element, Map<Key, any>> = new WeakMap()
  private dynamicComponentMountCounter = 0
  private dynamicComponentMountListeners = new Set<(el?: HTMLElement) => void>()

  constructor(private _aeppic: Aeppic) {}

  public getDynamicComponentMountCounter() {
    return this.dynamicComponentMountCounter
  }

  public markDynamicComponentMounted(el?: HTMLElement) {
    this.dynamicComponentMountCounter++

    const listeners = Array.from(this.dynamicComponentMountListeners)
    this.dynamicComponentMountListeners.clear()

    for (const listener of listeners) {
      listener(el)
    }
  }

  public onDynamicComponentMounted(callback: (el?: HTMLElement) => void) {
    this.dynamicComponentMountListeners.add(callback)

    return () => {
      this.dynamicComponentMountListeners.delete(callback)
    }
  }

  public forElement(el: HTMLElement) {
    return new SharedContextApiForComponents(el, this)
  }

  public forComponent() {
    return new SharedContextApiForDynamicComponent(this)
  }

  public set(kindColonName: string, value: any): any
  public set(el: HTMLElement, kindColonName: string, value: any): any
  public set(scope: string, kindColonName: string, value: any): any
  public set(a: HTMLElement | string, b: any, c?: any) {
    if (isElement(a)) {
      return this.replace(a, b, c)
    }

    this.reportDirectUse('set')
    return undefined
  }

  public setInScope(scope: string, kindColonName: string, value: any) {
    this.reportDirectUse('setInScope')
    return undefined
  }

  public replaceInScope(scope: string, kindColonName: string, value: any) {
    this.reportDirectUse('replaceInScope')
    return undefined
  }

  public patchInScope(scope: string, kindColonName: string, partial: any) {
    this.reportDirectUse('patchInScope')
    return undefined
  }

  public getInScope(scope: string, kindColonName: string) {
    this.reportDirectUse('getInScope')
    return Vue.observable({})
  }

  public findAllOfKindInScope(scope: string, kind: string) {
    this.reportDirectUse('findAllOfKindInScope')
    return []
  }

  public watchInScope(
    scope: string,
    kindColonName: string,
    cb: (newVal: any, oldVal: any) => void
  ) {
    this.reportDirectUse('watchInScope')
    return () => {}
  }

  private getGlobalScopeElement() {
    return this._aeppic.el || null
  }

  private reportDirectUse(methodName: string) {
    console.error(`Aeppic.SharedContext.${methodName}() cannot resolve a component scope. Use the controller SharedContext variable from mounted() or later, or call Aeppic.SharedContext.forElement(element).${methodName}(...).`)
  }

  // ---------- scope resolution ----------

  /**
   * Resolve nearest scope:
   * 1) closest [data-aeppic-sharedscope]
   * 2) app root fallback
   *
   * Why: deterministic, CSS-like shadowing; no global ambiguity.
   */
  private locateScopeElement(
    el: HTMLElement,
    { fallbackToGlobal = true } = {}
  ): HTMLElement | null {
    const local = findClosestElement(el, '[data-aeppic-sharedscope]') as HTMLElement | null
    if (local) return local

    if (!fallbackToGlobal) return null

    const root = findClosestElement(
      el,
      '[data-ae-version].ae-root, [data-ae-version]'
    ) as HTMLElement | null

    return root
  }

  private locateRootScopeElement(el: HTMLElement): HTMLElement | null {
    return findClosestElement(
      el,
      '[data-ae-version].ae-root, [data-ae-version]'
    ) as HTMLElement | null
  }

  /**
   * Find a named scope relative to an element (ancestor search only).
   * Why: avoids global querySelector ambiguity when multiple scopes share the same name.
   */
  private findNamedScopeUp(el: HTMLElement, scopeName: string): HTMLElement | null {
    let current: HTMLElement | null = el
    while (current) {
      if (current.dataset?.aeppicSharedscope === scopeName) {
        return current
      }
      current = current.parentElement
    }
    return null
  }

  // ---------- keys ----------

  /**
   * Strict parsing to avoid silent bugs.
   */
  private split(kindColonName: string): [string, string] {
    if (!kindColonName) {
      throw new Error('SharedContext key must not be empty')
    }
    const idx = kindColonName.indexOf(':')
    if (idx === -1) {
      return [kindColonName, 'default']
    }
    const kind = kindColonName.slice(0, idx)
    const name = kindColonName.slice(idx + 1)
    if (!kind) throw new Error('SharedContext key must have non-empty kind')
    if (!name) throw new Error('SharedContext key must have non-empty name')
    return [kind, name]
  }

  private makeKey(kind: string, name: string): Key {
    return `${kind}:${name}`
  }

  // ---------- storage ----------

  private getOrCreateScopeMap(scopeEl: HTMLElement): Map<Key, any> {
    if (!this.scopeData.has(scopeEl)) {
      this.scopeData.set(scopeEl, new Map())
    }
    return this.scopeData.get(scopeEl)!
  }

  /**
   * Always returns the same observable for (scope, key).
   * Why: reference stability is required for Vue reactivity across components.
   */
  /**
   * Deep-watch a shared observable, including added and removed keys.
   * Why: a getter returning `obs` itself only depends on the keys present when
   * the watcher is created. Reading it through a reactive holder also depends on
   * the object's own dep, which Vue.set / Vue.delete notify, so a watcher
   * registered before `set` still sees the keys `set` adds.
   */
  private watchObservable(obs: any, cb: (newVal: any, oldVal: any) => void): () => void {
    const holder = Vue.observable({ value: obs })
    return this._aeppic.Vue.$watch(
      () => holder.value,
      cb,
      { deep: true }
    )
  }

  private ensureObservable(scopeEl: HTMLElement, key: Key): any {
    const map = this.getOrCreateScopeMap(scopeEl)
    if (!map.has(key)) {
      map.set(key, Vue.observable({}))
    }
    return map.get(key)
  }

  /**
   * Replace semantics: clear then assign.
   * Why: explicit "set" = authoritative state.
   */
  private replaceValue(scopeEl: HTMLElement, key: Key, value: any) {
    const obs = this.ensureObservable(scopeEl, key)

    Object.keys(obs).forEach(k => Vue.delete(obs, k))

    if (value && typeof value === 'object') {
      Object.keys(value).forEach(k => Vue.set(obs, k, value[k]))
    }

    return obs
  }

  /**
   * Patch semantics: shallow merge.
   * Why: common for incremental UI updates.
   */
  private patchValue(scopeEl: HTMLElement, key: Key, partial: any) {
    const obs = this.ensureObservable(scopeEl, key)

    if (partial && typeof partial === 'object') {
      Object.keys(partial).forEach(k => Vue.set(obs, k, partial[k]))
    }

    return obs
  }

  // ---------- public API (element-relative) ----------

  public get(kindColonName: string): any
  public get(el: HTMLElement, kindColonName: string): any
  public get(elOrKindColonName: HTMLElement | string, kindColonName?: string) {
    const directRootUse = !isElement(elOrKindColonName)
    const el = isElement(elOrKindColonName) ? elOrKindColonName : this.getGlobalScopeElement()
    const resolvedKindColonName = isElement(elOrKindColonName) ? kindColonName : elOrKindColonName

    if (directRootUse) {
      this.reportDirectUse('get')
      return Vue.observable({})
    }

    if (!el) return Vue.observable({})

    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) {
      // Element not in a resolvable scope yet → return local placeholder (NOT shared globally)
      // Why: scope is unknown; sharing would be incorrect.
      return Vue.observable({})
    }

    const [k, n] = this.split(resolvedKindColonName)
    const key = this.makeKey(k, n)

    return this.ensureObservable(scopeEl, key)
  }

  public replace(kindColonName: string, value: any): any
  public replace(el: HTMLElement, kindColonName: string, value: any): any
  public replace(elOrKindColonName: HTMLElement | string, kindColonNameOrValue: any, maybeValue?: any) {
    const directRootUse = !isElement(elOrKindColonName)
    const el = isElement(elOrKindColonName) ? elOrKindColonName : this.getGlobalScopeElement()
    const resolvedKindColonName = isElement(elOrKindColonName) ? kindColonNameOrValue : elOrKindColonName
    const value = isElement(elOrKindColonName) ? maybeValue : kindColonNameOrValue

    if (directRootUse) {
      this.reportDirectUse('replace')
      return undefined
    }

    if (!el) return undefined

    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) return undefined

    const [k, n] = this.split(resolvedKindColonName)
    const key = this.makeKey(k, n)

    return this.replaceValue(scopeEl, key, value)
  }

  public patch(kindColonName: string, partial: any): any
  public patch(el: HTMLElement, kindColonName: string, partial: any): any
  public patch(elOrKindColonName: HTMLElement | string, kindColonNameOrPartial: any, maybePartial?: any) {
    const directRootUse = !isElement(elOrKindColonName)
    const el = isElement(elOrKindColonName) ? elOrKindColonName : this.getGlobalScopeElement()
    const resolvedKindColonName = isElement(elOrKindColonName) ? kindColonNameOrPartial : elOrKindColonName
    const partial = isElement(elOrKindColonName) ? maybePartial : kindColonNameOrPartial

    if (directRootUse) {
      this.reportDirectUse('patch')
      return undefined
    }

    if (!el) return undefined

    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) return undefined

    const [k, n] = this.split(resolvedKindColonName)
    const key = this.makeKey(k, n)

    return this.patchValue(scopeEl, key, partial)
  }

  public findAllOfKind(kind: string): any[]
  public findAllOfKind(el: HTMLElement, kind: string): any[]
  public findAllOfKind(elOrKind: HTMLElement | string, kind?: string) {
    const directRootUse = !isElement(elOrKind)
    const el = isElement(elOrKind) ? elOrKind : this.getGlobalScopeElement()
    const resolvedKind = isElement(elOrKind) ? kind : elOrKind

    if (directRootUse) {
      this.reportDirectUse('findAllOfKind')
      return []
    }

    if (!el) return []

    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) return []

    const map = this.scopeData.get(scopeEl)
    if (!map) return []

    const prefix = `${resolvedKind}:`
    const out: any[] = []
    for (const [key, obs] of map) {
      if (key.startsWith(prefix)) out.push(obs)
    }
    return out
  }

  // ---------- named scope (still element-relative) ----------

  public getInNamedScope(scope: string, kindColonName: string): any
  public getInNamedScope(el: HTMLElement, scope: string, kindColonName: string): any
  public getInNamedScope(elOrScope: HTMLElement | string, scopeOrKindColonName: string, maybeKindColonName?: string) {
    const directRootUse = !isElement(elOrScope)
    const el = isElement(elOrScope) ? elOrScope : this.getGlobalScopeElement()
    const scope = isElement(elOrScope) ? scopeOrKindColonName : elOrScope
    const kindColonName = isElement(elOrScope) ? maybeKindColonName : scopeOrKindColonName

    if (directRootUse) {
      this.reportDirectUse('getInNamedScope')
      return Vue.observable({})
    }

    if (!el) return Vue.observable({})

    const scopeEl = scope === 'global'
      ? this.locateRootScopeElement(el)
      : this.findNamedScopeUp(el, scope)

    if (!scopeEl) {
      // Same reasoning as get(): unknown scope → do not share globally
      return Vue.observable({})
    }

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.ensureObservable(scopeEl, key)
  }

  public replaceInNamedScope(scope: string, kindColonName: string, value: any): any
  public replaceInNamedScope(
    el: HTMLElement,
    scope: string,
    kindColonName: string,
    value: any
  ): any
  public replaceInNamedScope(
    elOrScope: HTMLElement | string,
    scopeOrKindColonName: string,
    kindColonNameOrValue: any,
    maybeValue?: any
  ) {
    const directRootUse = !isElement(elOrScope)
    const el = isElement(elOrScope) ? elOrScope : this.getGlobalScopeElement()
    const scope = isElement(elOrScope) ? scopeOrKindColonName : elOrScope
    const kindColonName = isElement(elOrScope) ? kindColonNameOrValue : scopeOrKindColonName
    const value = isElement(elOrScope) ? maybeValue : kindColonNameOrValue

    if (directRootUse) {
      this.reportDirectUse('replaceInNamedScope')
      return undefined
    }

    if (!el) return undefined

    const scopeEl =
      scope === 'global'
        ? this.locateRootScopeElement(el)
        : this.findNamedScopeUp(el, scope)

    if (!scopeEl) return undefined

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.replaceValue(scopeEl, key, value)
  }

  public patchInNamedScope(scope: string, kindColonName: string, partial: any): any
  public patchInNamedScope(
    el: HTMLElement,
    scope: string,
    kindColonName: string,
    partial: any
  ): any
  public patchInNamedScope(
    elOrScope: HTMLElement | string,
    scopeOrKindColonName: string,
    kindColonNameOrPartial: any,
    maybePartial?: any
  ) {
    const directRootUse = !isElement(elOrScope)
    const el = isElement(elOrScope) ? elOrScope : this.getGlobalScopeElement()
    const scope = isElement(elOrScope) ? scopeOrKindColonName : elOrScope
    const kindColonName = isElement(elOrScope) ? kindColonNameOrPartial : scopeOrKindColonName
    const partial = isElement(elOrScope) ? maybePartial : kindColonNameOrPartial

    if (directRootUse) {
      this.reportDirectUse('patchInNamedScope')
      return undefined
    }

    if (!el) return undefined

    const scopeEl =
      scope === 'global'
        ? this.locateRootScopeElement(el)
        : this.findNamedScopeUp(el, scope)

    if (!scopeEl) return undefined

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.patchValue(scopeEl, key, partial)
  }

  public findAllOfKindInNamedScope(scope: string, kind: string): any[]
  public findAllOfKindInNamedScope(el: HTMLElement, scope: string, kind: string): any[]
  public findAllOfKindInNamedScope(elOrScope: HTMLElement | string, scopeOrKind: string, maybeKind?: string) {
    const directRootUse = !isElement(elOrScope)
    const el = isElement(elOrScope) ? elOrScope : this.getGlobalScopeElement()
    const scope = isElement(elOrScope) ? scopeOrKind : elOrScope
    const kind = isElement(elOrScope) ? maybeKind : scopeOrKind

    if (directRootUse) {
      this.reportDirectUse('findAllOfKindInNamedScope')
      return []
    }

    if (!el) return []

    const scopeEl =
      scope === 'global'
        ? this.locateRootScopeElement(el)
        : this.findNamedScopeUp(el, scope)

    if (!scopeEl) return []

    const map = this.scopeData.get(scopeEl)
    if (!map) return []

    const prefix = `${kind}:`
    const out: any[] = []
    for (const [key, obs] of map) {
      if (key.startsWith(prefix)) out.push(obs)
    }
    return out
  }

  // ---------- watch ----------

  public watch(
    kindColonName: string,
    cb: (newVal: any, oldVal: any) => void
  ): () => void
  public watch(
    el: HTMLElement,
    kindColonName: string,
    cb: (newVal: any, oldVal: any) => void
  ): () => void
  public watch(
    elOrKindColonName: HTMLElement | string,
    kindColonNameOrCb: any,
    maybeCb?: any
  ): () => void {
    const directRootUse = !isElement(elOrKindColonName)
    const el = isElement(elOrKindColonName) ? elOrKindColonName : this.getGlobalScopeElement()
    const kindColonName = isElement(elOrKindColonName) ? kindColonNameOrCb : elOrKindColonName
    const cb = isElement(elOrKindColonName) ? maybeCb : kindColonNameOrCb

    if (directRootUse) {
      this.reportDirectUse('watch')
      return () => {}
    }

    if (!el) return () => {}

    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) {
      // No scope → no stable target to watch
      return () => {}
    }

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)
    const obs = this.ensureObservable(scopeEl, key)

    const unwatch = this.watchObservable(obs, cb)

    let called = false
    return () => {
      if (!called) {
        called = true
        unwatch()
      }
    }
  }

  public watchInNamedScope(
    scope: string,
    kindColonName: string,
    cb: (newVal: any, oldVal: any) => void
  ): () => void
  public watchInNamedScope(
    el: HTMLElement,
    scope: string,
    kindColonName: string,
    cb: (newVal: any, oldVal: any) => void
  ): () => void
  public watchInNamedScope(
    elOrScope: HTMLElement | string,
    scopeOrKindColonName: string,
    kindColonNameOrCb: any,
    maybeCb?: any
  ): () => void {
    const directRootUse = !isElement(elOrScope)
    const el = isElement(elOrScope) ? elOrScope : this.getGlobalScopeElement()
    const scope = isElement(elOrScope) ? scopeOrKindColonName : elOrScope
    const kindColonName = isElement(elOrScope) ? kindColonNameOrCb : scopeOrKindColonName
    const cb = isElement(elOrScope) ? maybeCb : kindColonNameOrCb

    if (directRootUse) {
      this.reportDirectUse('watchInNamedScope')
      return () => {}
    }

    if (!el) return () => {}

    const scopeEl =
      scope === 'global'
        ? this.locateRootScopeElement(el)
        : this.findNamedScopeUp(el, scope)

    if (!scopeEl) return () => {}

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)
    const obs = this.ensureObservable(scopeEl, key)

    const unwatch = this.watchObservable(obs, cb)

    let called = false
    return () => {
      if (!called) {
        called = true
        unwatch()
      }
    }
  }

  public destroy() {
    // no-op (watching uses global Vue instance)
  }
}

function isElement(value: any): value is HTMLElement {
  return !!value && typeof value === 'object' && value.nodeType === 1
}
